import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Outlet, useNavigate } from 'react-router'

import {
  SessionBoot,
  cognitoSignOut,
  setLogout,
  stopSessionRefresh,
  subscribeAuthSync,
} from '@penn/web-common'
import { useAppDispatch } from './stores/hooks'
import { LoginPage } from './pages/auth/LoginPage'
import { CallbackPage } from './pages/auth/CallbackPage'
import { NotFoundPage } from './pages/NotFoundPage'

// ── Admin console (existing, mounted under /admin) ─────────────────────────
import { GuestRoute, ProtectedRoute as AdminProtectedRoute } from './components/constant/ProtectedRoute'
import { RequireAdminAccess } from './components/constant/RequireAdminAccess'
import { RequireWorkForAccess } from './components/constant/RequireWorkForAccess'
import { WorkForPage } from './pages/work-for/WorkForPage'
import { WorkForEntry } from './pages/work-for/WorkForEntry'
import { RequirePagePermission, AdminIndexRedirect } from './components/constant/RequirePagePermission'
import { DashboardLayout } from './components/admin/DashboardLayout'
import { InternalSurfaceBand } from './components/admin/InternalSurfaceBand'
import { AdminEntry } from './components/admin/AdminEntry'
import { UsersPage } from './pages/users/UsersPage'
import { RolesPage } from './pages/users/RolesPage'
import { ApplicationsPage } from './pages/applications/ApplicationsPage'
import { TasksPage } from './pages/tasks/TasksPage'
import { ApprovalsPage } from './pages/tasks/ApprovalsPage'
import { SmtpPage } from './pages/smtp/SmtpPage'
import { AnalyticsPage } from './pages/analytics/AnalyticsPage'
import { ChangeLogPage } from './pages/changeLog/ChangeLogPage'
import { PlaceholderPage } from './pages/dashboard/PlaceholderPage'
import { OverviewPage } from './pages/dashboard/OverviewPage'
import { ProfilePage } from './pages/profile/ProfilePage'
import { MachineOptionsPage } from './pages/datasets/MachineOptionsPage'
import { ProductsPage } from './pages/datasets/ProductsPage'
import { LandingContentPage } from './pages/datasets/LandingContentPage'
import { Cad3dSettingsPage } from './pages/datasets/Cad3dSettingsPage'
import { CostModelsPage } from './pages/datasets/CostModelsPage'
import { CalculationParametersPage } from './pages/datasets/CalculationParametersPage'
import { ResponsibilityMapPage } from './pages/datasets/ResponsibilityMapPage'
import { ManualQuoteItemsPage } from './pages/datasets/ManualQuoteItemsPage'
import { FastenersPage } from './pages/datasets/FastenersPage'
import { ProfilFastenersPage } from './pages/datasets/ProfilFastenersPage'
import { AutorivFastenersPage } from './pages/datasets/AutorivFastenersPage'
import { AutorivConfiguratorItemsPage } from './pages/datasets/AutorivConfiguratorItemsPage'
import { SherexFastenersPage } from './pages/datasets/SherexFastenersPage'
import { SherexOptionsPage } from './pages/datasets/SherexOptionsPage'
import { ProfilIndieOptionsPage } from './pages/datasets/ProfilIndieOptionsPage'
import { PROFIL_INDIE_ELEMENT_TYPES_US } from './types/admin/profilIndieOptions.types'
import { FastenerToolingMappingPage } from './pages/datasets/FastenerToolingMappingPage'
import { ToolingSetsPage } from './pages/datasets/ToolingSetsPage'
import { JsonDatasetPage } from './pages/datasets/JsonDatasetPage'
import { PemserterPage } from './pages/datasets/PemserterPage'
import { CostModelToolsPage } from './pages/datasets/CostModelToolsPage'

/**
 * Cross-tab logout. When any other tab logs out this tab must tear down too;
 * it used to hang off the customer layout, which this host no longer has.
 */
function CrossTabLogout() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    return subscribeAuthSync(() => {
      stopSessionRefresh()
      void cognitoSignOut()
      dispatch(setLogout())
      // No state: LoginPage only auto-starts beginLogin() on an intent-carrying
      // navigation, so a bare arrival must not race /logout to /authorize.
      navigate('/login', { replace: true, state: null })
    })
  }, [dispatch, navigate])

  return null
}

/**
 * The auth routes sit outside DashboardLayout but must still say which host
 * they are: /login is the first page a confused tester sees.
 */
function AuthLayout() {
  return (
    <>
      <InternalSurfaceBand position="fixed" />
      <Outlet />
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <SessionBoot>
      <CrossTabLogout />
      <Routes>
        {/* Admin console — internal-staff-only, gated server-side. Its own chrome
            (DashboardLayout) so it never renders the customer NavBar. */}
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <RequireAdminAccess>
                <DashboardLayout />
              </RequireAdminAccess>
            </AdminProtectedRoute>
          }
        >
          <Route index element={<AdminIndexRedirect />} />

          {/* Settings — ungated; visible to any signed-in admin-console user
              regardless of which of the two permissions they hold. */}
          <Route path="settings/general" element={<PlaceholderPage title="General Settings" />} />
          <Route path="settings/permissions" element={<PlaceholderPage title="Permissions" />} />
          <Route path="settings/profile" element={<ProfilePage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings/security" element={<PlaceholderPage title="Security" />} />

          {/* Management — requires canManageUsers (mirrors Sidebar.tsx). */}
          <Route element={<RequirePagePermission permission="canManageUsers" />}>
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="responsibility-map" element={<ResponsibilityMapPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="change-log" element={<ChangeLogPage />} />
          </Route>

          {/* Dashboard / Datasets / Integrations — requires canManageMasterData
              (mirrors Sidebar.tsx). */}
          <Route element={<RequirePagePermission permission="canManageMasterData" />}>
            <Route path="dashboard/overview" element={<OverviewPage />} />
            <Route path="dashboard/reports" element={<PlaceholderPage title="Reports" />} />
            <Route path="smtp" element={<SmtpPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="datasets/machine-options" element={<MachineOptionsPage title="Machine Options (EU)" />} />
            <Route path="datasets/machine-options-us" element={<MachineOptionsPage title="Machine Options (NA)" subtitle="Dataset: Machine Options (NA)" regionId={2} />} />
            <Route path="datasets/products" element={<ProductsPage />} />
            <Route path="datasets/landing-content" element={<LandingContentPage />} />
            <Route path="datasets/cad-3d-settings" element={<Cad3dSettingsPage />} />
            <Route path="datasets/cost-models" element={<CostModelsPage />} />
            <Route path="datasets/calculation-parameters" element={<CalculationParametersPage />} />
            <Route path="datasets/cost-model-tools" element={<CostModelToolsPage />} />

            <Route path="datasets/manual-quote-items" element={<ManualQuoteItemsPage />} />
            <Route path="datasets/fasteners" element={<FastenersPage title="PEM Fasteners" />} />
            <Route path="datasets/profil-fasteners" element={<ProfilFastenersPage title="Profil Fasteners" />} />
            <Route path="datasets/autoriv-fasteners" element={<AutorivFastenersPage title="Autoriv Fasteners" />} />
            <Route path="datasets/sherex-fasteners" element={<SherexFastenersPage title="Sherex Fasteners" />} />
            <Route path="datasets/sherex-options" element={<SherexOptionsPage />} />
            <Route
              path="datasets/sherex-options-us"
              element={
                <SherexOptionsPage
                  title="Sherex Options (NA)"
                  subtitle="Dataset: Sherex Options (NA)"
                  currency="USD"
                />
              }
            />
            <Route path="datasets/autoriv-configurator-items" element={<AutorivConfiguratorItemsPage />} />
            <Route
              path="datasets/autoriv-configurator-items-us"
              element={
                <AutorivConfiguratorItemsPage
                  title="Autoriv Configurator Items (NA)"
                  subtitle="Dataset: Autoriv Configurator Items (NA)"
                  currency="USD"
                />
              }
            />
            <Route path="datasets/profil-indie-options" element={<ProfilIndieOptionsPage 
                 title="Profil InDie Options (EU)"
                  subtitle="Dataset: Record-level editor for Profil InDie options (EU)."
                  recordLabel="Profil InDie Options (EU)"/>} />
            <Route
              path="datasets/profil-indie-options-us"
              element={
                <ProfilIndieOptionsPage
                  title="Profil InDie Options (NA)"
                  subtitle="Dataset: Record-level editor for Profil InDie options (NA)."
                  recordLabel="Profil InDie Options (NA)"
                  regionId={2}
                  elementTypes={PROFIL_INDIE_ELEMENT_TYPES_US}
                />
              }
            />
            <Route path="datasets/fastener-tooling-mapping" element={<FastenerToolingMappingPage />} />
            <Route path="datasets/tooling-sets" element={<ToolingSetsPage />} />
            <Route path="datasets/pemserter-eur" element={<PemserterPage currency="EUR" />} />
            <Route path="datasets/pemserter-usd" element={<PemserterPage currency="USD" />} />
            <Route path="datasets/tasks-raw" element={<JsonDatasetPage title="Tasks (Raw JSON)" subtitle="Raw JSON editor for task records." />} />
          </Route>
        </Route>

        <Route element={<AuthLayout />}>
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          {/* OIDC redirect target. Registered in every build so the client's
              redirect URI is stable; inert (bounces to /login) otherwise. */}
          <Route path="/auth/callback" element={<CallbackPage />} />
        </Route>
        {/* Root lands staff in the console. There is no customer experience on
            this host any more — that is what web-customer is for. */}
        {/* Customer selection. Gated on canWorkForOthers only, so sales and
            engineering reach it without the admin console's permissions. */}
        <Route
          path="/work-for"
          element={
            <WorkForEntry>
              <RequireWorkForAccess>
                <WorkForPage />
              </RequireWorkForAccess>
            </WorkForEntry>
          }
        />

        <Route path="/" element={<AdminEntry />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </SessionBoot>
    </BrowserRouter>
  )
}
